import React, { Fragment, Suspense, useState } from 'react';
import { Button, Tooltip } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import {
  IInbound,
  IInboundItem,
  ISearchInboundReq,
  ISearchInboundRes,
} from '@/interfaces/inbound';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getInboundStatusColor,
  getInboundStatusName,
  getInboundTypeColor,
  getInboundTypeName,
} from '@/helpers/inbouldHelper';
import { IDataModal } from '@/interfaces/dataModal';
import InboundItemTableModal from './inbound-item-table-modal';
import InboundEditModal from './inbound-edit-modal';
import { EInboundStatus } from '@/enums/EInbound';
import { useAuth } from '@/contexts/AuthContext';
import { isEmployee } from '@/utils/permissionUtils';

interface IProps {
  showPagging?: boolean;
  loading: boolean;
  criteria: ISearchInboundReq;
  inbound: ISearchInboundRes;
  isFinance?: boolean;
  onDelete: (id: number) => void;
  onEditData: (item: IInbound) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IInbound> | SorterResult<IInbound>[],
  ) => void;
}

const _dataModal: IDataModal<IInboundItem[]> = {
  open: false,
};

const _editModal: IDataModal<Partial<IInbound>> = {
  open: false,
};

const InboundTable = ({
  loading,
  criteria,
  inbound,
  showPagging = true,
  isFinance = false,
  onTableChange,
  onDelete,
  onEditData,
}: IProps) => {
  dayjs.extend(relativeTime);
  const { userInfo } = useAuth();
  const [dataModal, setDataModal] = useState<IDataModal<IInboundItem[]>>({
    ..._dataModal,
  });
  const [editModal, setEditModal] = useState<IDataModal<Partial<IInbound>>>({
    ..._editModal,
  });

  const columns: TableProps<IInbound>['columns'] = [
    {
      title: 'ประเภท',
      dataIndex: 'type',
      key: 'type',
      align: 'center',
      width: 120,
      render: _ => {
        const name = getInboundTypeName(_);
        const status = getInboundTypeColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5 px-1',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      title: 'BU',
      dataIndex: 'bUs',
      key: 'bUs',
      align: 'center',
      width: 100,
    },
    {
      title: 'INV DATE',
      dataIndex: 'invDate',
      key: 'invDate',
      align: 'center',
      render: _ => formatDate(_),
      width: 120,
    },
    {
      title: 'INV NUMBER',
      dataIndex: 'invNO',
      key: 'invNO',
      className: 'font-bold text-green-700',
      align: 'center',
      width: 120,
    },
    {
      title: 'Shipment No.',
      dataIndex: 'ecS_JobNO',
      key: 'ecS_JobNO',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div className="flex flex-col">
          <div>{_}</div>
          <div>{rec.decNO}</div>
        </div>
      ),
    },
    {
      title: 'Products',
      dataIndex: 'decNO',
      key: 'decNO',
      render: (_, rec) => (
        <div className="text-xs">
          {rec?.inboundItems?.map((i, idx) => (
            <Fragment key={idx}>
              {`${i.pdtDescriptionE} (${formatNumber((i.baseUnitId === 'KG' ? i.totalQty : i.totalUnit) || 0)})`}
              <br />
            </Fragment>
          ))}
        </div>
      ),
    },
    {
      title: 'Arrival Date',
      dataIndex: 'arrivalDate',
      key: 'arrivalDate',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'Clear Date',
      dataIndex: 'clearDate',
      key: 'clearDate',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'WH Received',
      dataIndex: 'whReceivedDate',
      key: 'whReceivedDate',
      align: 'center',
      width: 120,
      render: _ => formatDate(_),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 120,
      render: (_, rec) => {
        const name = getInboundStatusName(_);
        const status = getInboundStatusColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5 px-1',
            )}
          >
            <Tooltip title={rec?.errorMessage} placement="left">
              {name}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'action',
      className: 'font-medium',
      align: 'center',
      width: 150,
      hidden: !!userInfo?.bu && isEmployee(),
      render: (_, rec) => {
        return (
          <div className="flex flex-row gap-2 justify-center">
            {isFinance ? (
              <>
                {rec.billingNo && (
                  <Button
                    ghost
                    type="primary"
                    size="small"
                    onClick={() => onEditData(rec)}
                  >
                    Cost Calculation
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  ghost
                  type="primary"
                  size="small"
                  onClick={() =>
                    setDataModal({
                      open: true,
                      item: rec.inboundItems,
                      optional: rec,
                    })
                  }
                >
                  {criteria?.status == EInboundStatus.PENDING
                    ? 'Approve'
                    : 'รายการ'}
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IInbound>
        columns={columns}
        criteria={criteria}
        data={inbound}
        loading={loading}
        onTableChange={onTableChange}
        scrollLength={1800}
        scroll
        showPagination={showPagging}
      />
      <Suspense>
        <InboundItemTableModal
          dataModal={dataModal}
          handleOk={function (): void {
            setDataModal({
              ..._dataModal,
            });
            onDelete(0);
          }}
          handleCancel={function (): void {
            setDataModal({
              ..._dataModal,
            });
            onDelete(0);
          }}
        />
        <InboundEditModal
          dataModal={editModal}
          handleOk={function (): void {
            setEditModal({
              ..._editModal,
            });
            onDelete(0);
          }}
          handleCancel={function (): void {
            setEditModal({
              ..._editModal,
            });
          }}
        />
      </Suspense>
    </div>
  );
};

export default InboundTable;
